import type {
  AgenticDecision,
  AgenticMatch,
} from "@inrange/building-manager-api-client";
import React from "react";
import { Badge, Button } from "react-bootstrap";

import {
  candidateMatchesName,
  citationDisplay,
  confColour,
  isSafeHttpUrl,
  parseWhyYesNo,
  recommendedCandidate,
  scrubResearchJargon,
  splitIntoPoints,
  tierColour,
  verdictDisplay,
} from "./leadResearchDisplay";

interface AiResearchResultProps {
  candidates: AgenticMatch[];
  decision: AgenticDecision | null | undefined;
  researchedAt?: string | null;
  // Provided only when this run's trace is available (id persisted on the row).
  onViewTrace?: () => void;
  // Which candidate to show. From a row's "Evidence" link -> that candidate; from
  // the tile's "View research" (null) -> the recommended party (partyToContact),
  // else the top candidate. The view is candidate-SCOPED: it shows this one
  // candidate's decision (only when it is the recommended party), why-this-rating,
  // and cited evidence - never every candidate at once (the candidates are rows in
  // the Landlord picker; related parties live in the panel's own expander).
  focusCandidate?: string | null;
}

const chipStyle: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  padding: "2px 7px",
  borderRadius: 5,
  color: "#fff",
};

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.3,
  color: "#6c757d",
  marginBottom: 6,
};

const section: React.CSSProperties = {
  padding: "12px 0",
  borderBottom: "1px solid #eef1f4",
};

const AiResearchResult: React.FC<AiResearchResultProps> = ({
  candidates,
  decision,
  researchedAt,
  onViewTrace,
  focusCandidate,
}) => {
  const d = decision ?? undefined;

  // Resolve the single candidate this view is scoped to.
  const party = d?.partyToContact?.trim() || "";
  const focused: AgenticMatch | null = (() => {
    if (focusCandidate) {
      const m = candidates.find((c) => candidateMatchesName(c, focusCandidate));
      if (m) return m;
    }
    // No explicit row clicked: fall back to the run's recommended party (the same
    // candidate the tile strip pills), so the tile and the modal name one party.
    return recommendedCandidate(candidates, party);
  })();

  const hasDecision = !!(
    d &&
    (d.verdict || d.decisionLine || d.partyToContact || d.nextAction)
  );
  // The run-level decision ("Proceed - write to X") only belongs on the party it
  // recommends. Opening a non-recommended candidate's evidence shows just that
  // candidate; it must not repeat "Proceed - write to <someone else>".
  const focusedIsRecommended =
    !!focused &&
    ((party && candidateMatchesName(focused, party)) ||
      (!party && !focusCandidate && focused === candidates[0]));
  const showDecision = hasDecision && (focusedIsRecommended || !focused);

  if (!showDecision && !focused) return null;

  const verdict = verdictDisplay(d?.verdict);

  // New-format reasoning renders as two titled, bulleted "Why yes" / "Why no"
  // sections; older single-blob rows (parse returns null) fall back to the single
  // "Why this rating" block below.
  const whyParsed = focused?.reasoning ? parseWhyYesNo(focused.reasoning) : null;
  const hasStructuredWhy =
    !!whyParsed && (whyParsed.yes.length > 0 || whyParsed.no.length > 0);
  const whyBulletList = (points: string[]): React.ReactNode => (
    <ul
      style={{
        margin: 0,
        paddingLeft: 18,
        fontSize: 13,
        color: "#3a3f4b",
        lineHeight: 1.5,
      }}
    >
      {points.map((point, i) => (
        <li key={i}>{point}</li>
      ))}
    </ul>
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          paddingBottom: 4,
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 700 }}>
          {focused ? focused.proprietorName : "AI research result"}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {researchedAt && (
            <span style={{ fontSize: 11, color: "#888" }}>
              {new Date(researchedAt).toLocaleString()}
            </span>
          )}
          {onViewTrace && (
            <Button
              size="sm"
              variant="link"
              style={{ fontSize: 12, padding: 0 }}
              onClick={onViewTrace}
            >
              View AI trace
            </Button>
          )}
        </span>
      </div>

      {focused && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            paddingBottom: 4,
          }}
        >
          <span
            style={{ ...chipStyle, background: confColour(focused.confidence) }}
          >
            {Math.round(focused.confidence)}%
          </span>
          {focused.companyNumber && (
            <span style={{ fontSize: 11.5, color: "#6c757d" }}>
              CH {focused.companyNumber}
            </span>
          )}
          {focused.matchGranularity && (
            <Badge
              bg="light"
              text="dark"
              style={{ fontSize: 10, fontWeight: 400 }}
            >
              {focused.matchGranularity}
            </Badge>
          )}
          {focused.notOnList && (
            <Badge
              bg="warning"
              text="dark"
              style={{ fontSize: 10, fontWeight: 500 }}
            >
              not on registry list
            </Badge>
          )}
        </div>
      )}

      {showDecision && (
        <div style={section}>
          <div style={sectionLabel}>Decision</div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {verdict.label && (
              <span style={{ ...chipStyle, background: verdict.colour }}>
                {verdict.label}
              </span>
            )}
            {d?.decisionLine && (
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>
                {scrubResearchJargon(d.decisionLine)}
              </span>
            )}
          </div>
          {d?.partyToContact && (
            <div style={{ fontSize: 12.5, marginTop: 4, color: "#495057" }}>
              <span style={{ color: "#6c757d" }}>Contact: </span>
              {d.partyToContact}
            </div>
          )}
          {d?.nextAction &&
            (() => {
              // Break a multi-step next-action into separate bullets; a single
              // step still renders inline as "Next: ...".
              const nextPoints = splitIntoPoints(d.nextAction);
              if (nextPoints.length <= 1) {
                return (
                  <div
                    style={{ fontSize: 12.5, marginTop: 2, color: "#495057" }}
                  >
                    <span style={{ color: "#6c757d" }}>Next: </span>
                    {nextPoints[0] ?? scrubResearchJargon(d.nextAction)}
                  </div>
                );
              }
              return (
                <div style={{ fontSize: 12.5, marginTop: 4, color: "#495057" }}>
                  <div style={{ color: "#6c757d", marginBottom: 2 }}>Next</div>
                  <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                    {nextPoints.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                </div>
              );
            })()}
        </div>
      )}

      {focused?.reasoning &&
        (hasStructuredWhy && whyParsed ? (
          <div style={section}>
            {whyParsed.yes.length > 0 && (
              <div>
                <div style={sectionLabel}>Why yes</div>
                {whyBulletList(whyParsed.yes)}
              </div>
            )}
            {whyParsed.no.length > 0 && (
              <div style={{ marginTop: whyParsed.yes.length > 0 ? 12 : 0 }}>
                <div style={sectionLabel}>Why no</div>
                {whyBulletList(whyParsed.no)}
              </div>
            )}
          </div>
        ) : (
          <div style={section}>
            <div style={sectionLabel}>Why this rating</div>
            <div style={{ fontSize: 13, color: "#3a3f4b", lineHeight: 1.5 }}>
              {scrubResearchJargon(focused.reasoning)}
            </div>
          </div>
        ))}

      {focused && focused.citations.length > 0 && (
        <div style={{ ...section, borderBottom: "none" }}>
          <div style={sectionLabel}>Evidence - {focused.proprietorName}</div>
          <ul style={{ margin: 0, padding: 0, listStyle: "none", fontSize: 12.5 }}>
            {focused.citations.map((cit, i) => {
              const disp = citationDisplay(cit.title, cit.url);
              return (
                <li
                  key={`${cit.url}:${i}`}
                  style={{
                    padding: "6px 0",
                    borderTop: i === 0 ? "none" : "1px dashed #eef1f4",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    {cit.tier && (
                      <span
                        style={{
                          ...chipStyle,
                          fontSize: 10,
                          padding: "1px 5px",
                          background: tierColour(cit.tier),
                        }}
                        title="Evidence tier (A registry-grade, D weak)"
                      >
                        {cit.tier.toUpperCase()}
                      </span>
                    )}
                    {isSafeHttpUrl(cit.url) ? (
                      <a
                        href={cit.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={disp.tooltip ?? undefined}
                        style={{ fontWeight: 600 }}
                      >
                        {disp.label}
                      </a>
                    ) : (
                      <span title={disp.tooltip ?? undefined}>{disp.label}</span>
                    )}
                    {cit.publishedDate && (
                      <span style={{ color: "#888" }}>({cit.publishedDate})</span>
                    )}
                  </span>
                  {cit.quote && (
                    <span
                      style={{
                        display: "block",
                        color: "#666",
                        fontStyle: "italic",
                        marginTop: 2,
                      }}
                    >
                      &ldquo;{cit.quote}&rdquo;
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AiResearchResult;
